/**
 * Утилитные функции для приложения
 */

/**
 * Debounce функция - задержка выполнения
 * @param {Function} func - Функция для выполнения
 * @param {number} wait - Время задержки в мс
 * @param {boolean} immediate - Выполнить сразу
 * @returns {Function} - Обернутая функция
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle функция - ограничение частоты выполнения
 * @param {Function} func - Функция для выполнения
 * @param {number} limit - Интервал в мс
 * @returns {Function} - Обернутая функция
 */
export function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
}

/**
 * Поиск элемента в DOM с проверкой существования
 * @param {string} selector - CSS селектор
 * @param {Element} parent - Родительский элемент
 * @returns {Element|null} - Найденный элемент
 */
export function querySelector(selector, parent = document) {
  const element = parent.querySelector(selector);
  if (!element) {
    console.warn(`Элемент с селектором "${selector}" не найден`);
  }
  return element;
}

/**
 * Поиск всех элементов в DOM
 * @param {string} selector - CSS селектор
 * @param {Element} parent - Родительский элемент
 * @returns {NodeList} - Найденные элементы
 */
export function querySelectorAll(selector, parent = document) {
  return parent.querySelectorAll(selector);
}

/**
 * Создание DOM элемента с атрибутами
 * @param {string} tag - Тег элемента
 * @param {Object} attributes - Атрибуты элемента
 * @param {string} content - Содержимое элемента
 * @returns {Element} - Созданный элемент
 */
export function createElement(tag, attributes = {}, content = '') {
  const element = document.createElement(tag);

  Object.entries(attributes).forEach(([key, value]) => {
    if (key === 'className') {
      element.className = value;
    } else if (key === 'innerHTML') {
      element.innerHTML = value;
    } else if (key === 'textContent') {
      element.textContent = value;
    } else if (key.startsWith('data-')) {
      element.setAttribute(key, value);
    } else {
      element[key] = value;
    }
  });

  if (content) {
    element.innerHTML = content;
  }

  return element;
}

/**
 * Форматирование продолжительности фильма
 * @param {number} duration - Продолжительность в минутах
 * @returns {string} - Отформатированная строка
 */
export function formatDuration(duration) {
  if (typeof duration === 'number') {
    return `${duration} мин`;
  }
  return duration;
}

/**
 * Экранирование HTML символов
 * @param {string} text - Исходный текст
 * @returns {string} - Экранированный текст
 */
export function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Нормализация строки для поиска
 * @param {string} text - Исходный текст
 * @returns {string} - Нормализованный текст
 */
export function normalizeSearchText(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Проверка, содержит ли текст поисковый запрос
 * @param {string} text - Текст для поиска
 * @param {string} query - Поисковый запрос
 * @returns {boolean} - Результат поиска
 */
export function textContains(text, query) {
  if (!text || !query) return false;
  return normalizeSearchText(text).includes(normalizeSearchText(query));
}

/**
 * Плавная прокрутка к элементу
 * @param {Element|string} target - Элемент или селектор
 * @param {number} offset - Смещение в пикселях
 * @param {string} behavior - Тип прокрутки
 */
export function scrollToElement(target, offset = 0, behavior = 'smooth') {
  const element = typeof target === 'string' ? querySelector(target) : target;
  if (!element) return;

  const elementTop = element.offsetTop - offset;
  window.scrollTo({
    top: elementTop,
    behavior,
  });
}

/**
 * Получение позиции прокрутки
 * @returns {number} - Позиция прокрутки
 */
export function getScrollPosition() {
  return window.pageYOffset || document.documentElement.scrollTop;
}

/**
 * Проверка видимости элемента
 * @param {Element} element - Элемент для проверки
 * @returns {boolean} - Видимость элемента
 */
export function isElementVisible(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Получение размеров viewport
 * @returns {Object} - Ширина и высота
 */
export function getViewportSize() {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

/**
 * Проверка мобильного устройства
 * @returns {boolean} - Мобильное устройство или нет
 */
export function isMobile() {
  return getViewportSize().width <= 768;
}

/**
 * Анимация появления элемента
 * @param {Element} element - Элемент для анимации
 * @param {string} animationClass - CSS класс анимации
 * @param {number} delay - Задержка в мс
 */
export function animateElement(element, animationClass = 'fade-in', delay = 0) {
  setTimeout(() => {
    element.classList.add(animationClass);
  }, delay);
}

/**
 * Копирование текста в буфер обмена
 * @param {string} text - Текст для копирования
 * @returns {Promise<boolean>} - Успешность операции
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Ошибка копирования в буфер обмена:', error);
    return false;
  }
}

/**
 * Генерация уникального ID
 * @param {string} prefix - Префикс ID
 * @returns {string} - Уникальный ID
 */
export function generateId(prefix = 'id') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Форматирование числа с разделителями
 * @param {number} number - Число для форматирования
 * @returns {string} - Отформатированное число
 */
export function formatNumber(number) {
  return new Intl.NumberFormat('ru-RU').format(number);
}
